import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Scripts,
  ScrollRestoration,
  useLocation,
  data,
  LoaderFunctionArgs,
  useLoaderData,
  Meta,
  Links,
  useRouteError,
  isRouteErrorResponse,
  Outlet,
  ActionFunctionArgs,
  replace,
  Link,
  useNavigate,
  useNavigation,
  NavigateFunction,
  LinksFunction,
} from 'react-router';
import './app.css';
import { useChangeLanguage } from 'remix-i18next/react';
import {
  ThemeProvider,
  useTheme,
  PreventFlashOnWrongTheme,
  createThemeAction,
  Theme,
} from 'remix-themes';

import { version } from 'devops/version';
import { getClientEnv, createEnvScript } from '~/core/env';
import { getLocale, i18nextMiddleware, localeCookie, langCookie } from '~/core/i18n/i18next';
import { validateRouteAccess } from '~/core/router/route_middleware.server';
import { PlatformRoleEnum, AppRouteKey, PersonaRoleEnum } from '~/core/router/route_types';
import { getRouteFromPath, AppRouteKeys } from '~/core/router/router_utils';
import { useNavigationStore } from '~/core/stores/navigation_store';
import { themeSessionResolver } from '~/core/utils/theme_session';
import { getAuthSession, getUserFromToken } from '~/features/auth/application/auth_session';
import { AppLayoutWrapper } from '~/shared/layouts/app_layout_wrapper';
import { getSidebarConfigByRole } from '~/shared/sidebar/base_sidebar_config';
import { Sidebar } from '~/shared/sidebar/sidebar';

import { GetCountryGeographyStructuresExtendedEntity } from './common_entities/region_address/country_geography_structures_extended_input_entity';
import { OrgSettingListResponse } from './common_models/organization_settings_list_model';
import { createAction, createLoader } from './core/middleware/loader_action_middleware';
import { AppRoutes } from './core/router/routes';
import { SidebarPropsModel } from './datasources/sidebar_datasource/models/sidebar_props';
import { UserMetadata } from './features/auth/domain/entities/user_metadata';
import { LogoutUseCase } from './features/auth/domain/usecases/command/logout_command';
import { GetCountriesQuery } from './features/auth/domain/usecases/query/get_countries_query';
import { GetUserMetadataUseCase } from './features/auth/domain/usecases/query/get_user_metadata_query';
import { OrganizationSettingsHandler } from './features/organization_settings/application/org_settings_handler';
import {
  getOrgSettingForSettingType,
  OrganizationSettingKey,
  OrganizationSettingType,
} from './features/organization_settings/domain/constant';
import { BulkInviteHandler } from './features/user_management/application/bulk_invite_handler';
import { InviteUserHandler } from './features/user_management/application/invite_user_handler';
import { BulkUploadHistory } from './features/user_management/domain/entities/bulk_upload_history';
import AnimatedSandClock from './shared/components/SandClock';
import 'mapbox-gl/dist/mapbox-gl.css';

export const unstable_middleware = [i18nextMiddleware];
const logoutUseCase = new LogoutUseCase();
const getCountriesQuery = new GetCountriesQuery();

// Define the root loader data type
export type RootLoaderData = {
  locale: string;
  theme: Theme | null;
  client: {
    PUBLIC_API_URL: string;
    PUBLIC_APP_VERSION: string;
  };
  userMetadata: UserMetadata | null;
  sidebarConfig: SidebarPropsModel | null;
  currentLoggedInUser: UserMetadata | null;
  personaRole: PersonaRoleEnum | null;
  orgSettings: [OrgSettingListResponse, GetCountryGeographyStructuresExtendedEntity];
  countries: any[];
};
declare class DataWithResponseInit<D> {
  type: string;
  data: D;
  init: ResponseInit | null;
  constructor(data: D, init?: ResponseInit);
}
export const loader = createLoader<DataWithResponseInit<RootLoaderData> | Response>(
  async ({ request, context }: LoaderFunctionArgs, authToken) => {
    const getUserMetadataUseCase = new GetUserMetadataUseCase();
    const authSession = await getAuthSession(request);
    let userMetadata = null;
    if (authSession.getAuthToken()) {
      userMetadata = await getUserMetadataUseCase.execute(authSession.getAuthToken()!);
    }
    const locale = getLocale(context);
    const { getTheme } = await themeSessionResolver(request);
    const navHeader = request.headers.get('X-Client-Navigation');
    let clientNavigationContext:
      | {
          isClientNavigation: boolean;
          fromRoute?: string;
        }
      | undefined;
    if (navHeader === 'true') {
      const navFromHeader = request.headers.get('X-Client-Nav-From');
      clientNavigationContext = {
        isClientNavigation: true,
        fromRoute: navFromHeader || '',
      };
    }
    const validationResult = await validateRouteAccess(request, userMetadata, {
      clientNavigation: clientNavigationContext,
    });
    if (validationResult.status !== 200) {
      return validationResult;
    }

    // Get client-side environment for data passing (safe for client use)
    const clientEnv = getClientEnv();
    let currentLoggedInUser: UserMetadata | null = null;
    let personaRole: PersonaRoleEnum | null = null;

    // Get sidebar configuration based on user role
    let sidebarConfig = null;
    if (userMetadata) {
      // get the current auth token
      const currentAccessToken = authSession.getAuthToken()?.accessToken;
      currentLoggedInUser = await getUserFromToken(currentAccessToken ?? '');

      // get the current organization
      const currentOrg = userMetadata?.organizations?.find((org) => org.isCurrent);
      const platformRole = currentOrg?.platformRole?.platformRoleName as PlatformRoleEnum;

      // Get the current persona role from the organization
      const currentPersonaRole = currentOrg?.personaRoles?.find((role) => role.isCurrent);
      personaRole = currentPersonaRole?.personaRoleName as PersonaRoleEnum;
      if (platformRole) {
        sidebarConfig = await getSidebarConfigByRole(platformRole, personaRole);
      }
    }
    let bulkUploadHistory: BulkUploadHistory[] = [];
    if (authToken) {
      bulkUploadHistory = await BulkInviteHandler.getBulkUploadHistory(authToken);
    }

    // Fetch countries from CountriesDataSource
    const countries = await getCountriesQuery.execute();
    const orgSettingsResult = await OrganizationSettingsHandler.getRolesManagementLoader({
      request,
      context,
      params: {},
    });

    // Initialize with default values
    let orgSettings: [OrgSettingListResponse[], GetCountryGeographyStructuresExtendedEntity] = [
      [],
      // Default empty array for OrgSettingListResponse[]
      {} as GetCountryGeographyStructuresExtendedEntity, // Default empty object for GetCountryGeographyStructuresExtendedEntity
    ];
    if (Array.isArray(orgSettingsResult)) {
      orgSettings = orgSettingsResult as unknown as [
        OrgSettingListResponse[],
        GetCountryGeographyStructuresExtendedEntity,
      ];
    } else {
      console.error(
        'Failed to load organization settings: Invalid response format. Using default empty settings.'
      );
    }

    const orgLanguage = getOrgSettingForSettingType(
      OrganizationSettingKey.LANGUAGE_SETTINGS,
      orgSettings[0]
    )?.stringValue;

    // Set language cookie - prefer organization setting, fallback to locale
    const finalLanguage = orgLanguage || locale;
    const headers = new Headers();

    // Also set a simple 'lang' cookie with plain text value for compatibility with getUserLocale
    headers.append('Set-Cookie', await langCookie.serialize(finalLanguage));

    return data(
      {
        locale: locale,
        theme: getTheme(),
        client: clientEnv,
        userMetadata: userMetadata,
        sidebarConfig: sidebarConfig,
        currentLoggedInUser: currentLoggedInUser,
        personaRole: personaRole,
        bulkUploadHistory,
        orgSettings,
        countries,
        version,
      },
      {
        headers,
      }
    );
  }
);
const themeAction = createThemeAction(themeSessionResolver);
export const action = createAction(async (args: ActionFunctionArgs) => {
  const { request } = args;
  const formData = await args.request.formData();
  const action = formData.get('actionType') as string;
  const authSession = await getAuthSession(request);
  const authToken = authSession.getAuthToken();
  if (!authToken) {
    // This should not happen if the user is logged in, but as a safeguard:
    return new Response('Unauthorized', {
      status: 401,
    });
  }
  if (action === 'inviteUser') {
    return InviteUserHandler.handleUserInvite(formData, request, authToken);
  } else if (action === 'bulkInvite') {
    return BulkInviteHandler.handleBulkInvite(formData, authToken);
  }
  if (action === 'logout') {
    const authSession = await getAuthSession(request);
    const authToken = authSession.getAuthToken();
    if (authToken) {
      await logoutUseCase.execute(authToken);
    }

    // Get the selected organization and user email from form data
    const selectedOrg = formData.get('selectedOrganization');
    const userEmail = formData.get('userEmail');

    // Create URL with parameters
    const loginUrl = new URL(AppRoutes.LOGIN.path, request.url);
    if (selectedOrg) {
      loginUrl.searchParams.set('selectedOrganization', selectedOrg.toString());
    }
    if (userEmail) {
      loginUrl.searchParams.set('userEmail', userEmail.toString());
    }

    // Clear all cookies on logout
    const headers = new Headers();
    headers.append('Set-Cookie', await authSession.destroy());
    headers.append('Set-Cookie', await langCookie.serialize('', { maxAge: 0 }));
    headers.append('Set-Cookie', await localeCookie.serialize('', { maxAge: 0 }));

    return replace(loginUrl.toString(), { headers });
  }
  const themeResponse = await themeAction(args);
  return themeResponse;
});

// 🔁 Reusable HtmlShell component
function Providers({ children }: { children: React.ReactNode }) {
  const loaderData = useLoaderData<typeof loader>();
  return (
    <ThemeProvider
      specifiedTheme={loaderData?.theme ?? null}
      themeAction="/action/set-theme"
      disableTransitionOnThemeChange={true}
    >
      {children}
    </ThemeProvider>
  );
}
function HtmlShell({ children }: { children: React.ReactNode }) {
  const loaderData = useLoaderData<typeof loader>();
  const { i18n } = useTranslation();
  useChangeLanguage(loaderData?.locale ?? 'en');
  const [theme] = useTheme();
  useEffect(() => {
    console.log('re render');
    if (Array.isArray(loaderData.orgSettings) && loaderData.orgSettings.length > 0) {
      const defaultLanguage = getOrgSettingForSettingType(
        OrganizationSettingKey.LANGUAGE_SETTINGS,
        loaderData.orgSettings[0]
      )?.stringValue;
      i18n.changeLanguage(defaultLanguage ?? 'en');
    }
    return () => {
      // Cleanup function if needed
      i18n.changeLanguage('en');
    };
  }, [loaderData]);
  return (
    <html
      lang={i18n.language}
      dir={i18n.dir(i18n.language)}
      data-theme={theme ?? null}
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1"
        />
        <Meta />
        <PreventFlashOnWrongTheme ssrTheme={Boolean(theme)} />
        <Links />
      </head>
      <body>{children}</body>
    </html>
  );
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setRoute } = useNavigationStore();
  const { sidebarConfig } = useLoaderData<typeof loader>();

  const setupLogoutListener = (navigate: NavigateFunction) => {
    const channel = new BroadcastChannel('auth');

    channel.onmessage = (event) => {
      if (event.data.type === 'logout') {
        const currentPath = window.location.pathname;

        // Don't navigate if we're on verify-invite page
        if (!currentPath.includes('/verify-invite')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        } else {
          // Just clear auth data, let form submission handle navigation
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
    };

    return () => channel.close();
  };

  useEffect(() => {
    const cleanup = setupLogoutListener(navigate);

    // Cleanup when component unmounts
    return cleanup;
  }, [navigate]);

  useEffect(() => {
    // This ensures we're only running on the client side
    if (typeof window !== 'undefined') {
      const currentPath = location.pathname;
      const routeKey = getRouteFromPath(currentPath) as AppRouteKeys;
      if (routeKey) {
        console.log(`📍[App] Initializing current route to ${routeKey} from path ${currentPath}`);
        setRoute(routeKey);
      }
    }
  }, [location.pathname, setRoute]);

  return (
    <Providers>
      <HtmlShell>
        <AppLayoutWrapper>
          {/* Render the sidebar with metadata and groups */}

          {/* <Sidebar
            metadata={
              sidebarConfig?.metadata ?? {
                version: '1.0',
                theme: 'default',
              }
            }
            groups={sidebarConfig?.groups ?? []}
          /> */}
          <Outlet />
        </AppLayoutWrapper>
        <ScrollRestoration />
        {/* Import the script element to inject environment variables */}
        {typeof document === 'undefined' ? (
          <script dangerouslySetInnerHTML={createEnvScript(getClientEnv())} />
        ) : null}
        <Scripts />
      </HtmlShell>
    </Providers>
  );
}
export function ErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation(['common']);
  const content = isRouteErrorResponse(error) ? (
    <>
      <h4 className="text-center text-[56px] font-bold text-errorDefault">
        {error.status === 404 ? '404' : t('title')}
      </h4>
      <p className="text-center text-[24px] font-bold text-neutralDark">
        {error.status === 404 ? 'Page Not Found' : `${error.status} ${error.statusText}`}
      </p>
      {error.status === 404 && (
        <p className="mt-4 text-center text-neutralSemiDark">
          {t('the_page_you_are_looking_for_doesn_t_exist_or_has_')}
        </p>
      )}
    </>
  ) : (
    <>
      <h4 className="h4 text-center text-errorDefault">{t('unexpected')}</h4>
      <p className="text-center text-neutralDark">
        {error instanceof Error ? error.message : 'An unexpected error occurred'}
      </p>
    </>
  );
  return (
    <Providers>
      <HtmlShell>
        <div className="bg-neutralExtralight flex h-screen items-center justify-center">
          <div className="w-full max-w-md space-y-8 rounded-lg bg-neutralWhite p-8 shadow-sm">
            <div className="mx-auto mb-12">
              <div className="relative mx-auto flex h-40 w-40 items-center justify-center">
                <AnimatedSandClock />
              </div>
            </div>
            {content}
            <div className="mt-6 flex justify-center">
              <Link
                to={AppRoutes[AppRouteKey.HOME].path}
                className="rounded-md bg-primaryDefault px-12 py-4 text-center font-medium text-white hover:bg-primaryDark"
              >
                {t('return_to_home')}
              </Link>
            </div>
          </div>
        </div>
      </HtmlShell>
    </Providers>
  );
}
